import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { QuillContentPdf } from '../../../components/rich-text-editor/quill-to-pdf';
import logo from '../../../images/Logo2.png';
import { formatNumber } from '../../../utils/utils';
import {
  calculateBudgetForDeliverable,
  calculateBudgetForProject,
  calculateBudgetForTask,
} from '../milestone/utils/budget.utils';
import { DISCOUNT_OPTIONS } from '../planner.constants';
import { getDiscountedValue } from '../totals/planner-totals.utils';
import ProximaRegular from '../../../theme/fonts/ProximaNova-Reg.otf';
import ProximaBold from '../../../theme/fonts/ProximaNova-Bold.otf';
import ProximaItalic from '../../../theme/fonts/ProximaNova-RegIt.otf';
import ProximaBoldItalic from '../../../theme/fonts/ProximaNova-BoldIt.otf';
import { SignatureBlock } from './estimate-pdf-ffd';

Font.register({
  family: 'Proxima Nova',
  fonts: [
    { src: ProximaRegular, fontWeight: 'normal' },
    { src: ProximaBold, fontWeight: 'bold' },
    { src: ProximaItalic, fontWeight: 'normal', fontStyle: 'italic' },
    { src: ProximaBoldItalic, fontWeight: 'bold', fontStyle: 'italic' },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Proxima Nova',
    padding: 50,
    paddingBottom: 80,
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  font: {
    fontSize: 10,
    lineHeight: '115%',
  },
  font2: {
    fontSize: 10,
    lineHeight: '150%',
  },
  logo: { width: 70 },
  headerRight: {
    textAlign: 'right',
    width: '100%',
  },
  bold: {
    fontFamily: 'Proxima Nova',
    fontWeight: 'bold',
  },
  text12: { fontSize: 12 },
  text9: { fontSize: 9 },
  title: {
    borderBottomWidth: 0.5,
    borderBottomColor: 'black',
    borderBottomStyle: 'solid',
    marginTop: 15,
  },
  borderTop: {
    borderTopWidth: 0.5,
    borderTopColor: 'black',
    borderTopStyle: 'solid',
    paddingTop: 5,
  },
  divider: {
    borderBottomWidth: 0.5,
    borderBottomColor: 'black',
    borderBottomStyle: 'solid',
    width: '100%',
  },
  table: {
    marginTop: 10,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 5,
  },
  colLarge: {
    width: '70%',
  },
  col15: {
    width: '15%',
  },
  col15Last: { width: '15%' },
  cellRight: { textAlign: 'right' },
  leftIndent: { marginLeft: 30 },
  termsContainer: {
    paddingTop: 8,
    paddingBottom: 30,
    marginTop: 'auto',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 50,
    right: 50,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    fontSize: 5,
    height: 50,
    paddingBottom: 5,
    backgroundColor: 'white',
  },
  pageCounter: {
    textAlign: 'right',
    fontSize: 9,
    paddingTop: 5,
  },
});

const Header = ({ flowId, date }) => (
  <View style={[styles.headerContainer, styles.font2]}>
    <View>
      <Image src={logo} style={styles.logo} />
    </View>
    <View style={styles.headerRight}>
      <Text style={styles.text12}>ESTIMATE: {flowId}</Text>
      <Text style={[styles.bold]}>{date}</Text>
      <Text style={{ marginTop: 10 }}>FKA</Text>
      <Text>1700, 10123 99 Street</Text>
      <Text>Edmonton, AB T5J 3H1</Text>
      <Text style={{ marginTop: 10 }}>fka.agency | 780 628 5622</Text>
    </View>
  </View>
);

const Footer = () => (
  <View style={styles.footer} fixed>
    <View style={styles.divider} />
    <Text
      style={[styles.pageCounter, styles.bold]}
      render={({ pageNumber, totalPages }) => `${pageNumber}`}
    />
  </View>
);

const EstimatePdf = ({ projectData, materials, discountObj, companyDetails, contactName }) => {
  const now = new Date();
  const currentDate = now.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const actualProjectData = projectData.projectData || projectData || {};

  const {
    projectName = 'Untitled Project',
    projectScope = 'No project scope provided.',
    flowId = projectData.id,
  } = actualProjectData;

  // budget calculations
  const deliverables = projectData.milestones || projectData.deliverables || [];
  const resources = projectData.resources || [];
  const { value: servicesCost } = calculateBudgetForProject(deliverables, resources);

  const materialsCost = materials ? materials.reduce((sum, m) => sum + Number(m.total || 0), 0) : 0;

  const { serviceDiscount = 0, serviceDiscountUnit } = discountObj;
  const { materialDiscount = 0, materialDiscountUnit } = discountObj;

  const serviceCostAfterDiscount = getDiscountedValue(
    servicesCost,
    serviceDiscount,
    serviceDiscountUnit,
  );
  const materialsCostAfterDiscount = getDiscountedValue(
    materialsCost,
    materialDiscount,
    materialDiscountUnit,
  );
  const totalCostAfterDiscount = serviceCostAfterDiscount + materialsCostAfterDiscount;

  const hasMaterials = materials && materials.some((m) => m.description);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <Header flowId={flowId} date={currentDate} companyDetails={companyDetails} />
        {/* Client Info */}
        <View style={[{ marginTop: 15 }, styles.font2]}>
          {contactName && <Text style={styles.bold}>Attention: {contactName}</Text>}
          <Text style={styles.bold}>{companyDetails.companyName}</Text>
          <Text>{companyDetails.streetAddress}</Text>
          <Text>
            {companyDetails.city}, {companyDetails.province}
            {` ${companyDetails.postalCode}`}
          </Text>
        </View>
        {/* Project Title & Scope */}
        <View style={[{ marginTop: 25 }, styles.font2]}>
          <Text style={[styles.text12, styles.bold]}>{projectName}</Text>
          <QuillContentPdf
            html={projectScope}
            style={{ marginTop: 15 }}
            textStyle={{ fontSize: 10, lineHeight: 1.5 }}
          />
        </View>
        {/* Services Table */}
        <View style={[styles.table, styles.font2]}>
          {/* Header Row */}
          <View style={[styles.tableRow, styles.title]}>
            <View style={[styles.colLarge]}>
              <Text style={styles.bold}>Services</Text>
            </View>
            <View style={styles.col15}>
              <Text style={[styles.bold, styles.cellRight]}>Hours</Text>
            </View>
            <View style={styles.col15Last}>
              <Text style={[styles.bold, styles.cellRight]}>Amount</Text>
            </View>
          </View>

          {/* Data Rows */}
          {deliverables.map((d, i) => {
            const { hours, value } = calculateBudgetForDeliverable(d, resources);
            return (
              <View key={i} style={[{ marginBottom: 5 }]}>
                <View style={[styles.tableRow]}>
                  <View style={styles.colLarge}>
                    <Text style={styles.bold}>{d.name}</Text>
                  </View>
                  <View style={styles.col15}>
                    <Text style={[styles.bold, styles.cellRight]}>{formatNumber(hours)}</Text>
                  </View>
                  <View style={styles.col15Last}>
                    <Text style={[styles.bold, styles.cellRight]}>${formatNumber(value)}</Text>
                  </View>
                </View>
                {d.tasks.map((t, ti) => {
                  const { hours: th, value: tv } = calculateBudgetForTask(t, resources);
                  return (
                    <View key={ti} style={[styles.tableRow, { marginTop: 5 }]}>
                      <View style={styles.colLarge}>
                        <Text style={styles.leftIndent}>{t.description}</Text>
                      </View>
                      <View style={styles.col15}>
                        <Text style={styles.cellRight}>{formatNumber(th)}</Text>
                      </View>
                      <View style={styles.col15Last}>
                        <Text style={styles.cellRight}>${formatNumber(tv)}</Text>
                      </View>
                    </View>
                  );
                })}
              </View>
            );
          })}

          {/* Subtotals & Discounts */}
          <View style={[styles.tableRow, styles.borderTop]}>
            <View style={[styles.colLarge, { width: '85%' }]}>
              <Text style={[styles.bold, styles.cellRight]}>Subtotal</Text>
            </View>
            <View style={styles.col15Last}>
              <Text style={[styles.bold, styles.cellRight]}>${formatNumber(servicesCost)}</Text>
            </View>
          </View>
          {serviceDiscount !== 0 && (
            <View style={styles.tableRow}>
              <View style={[styles.colLarge, { width: '85%' }]}>
                <Text style={[styles.bold, { marginLeft: '80%' }]}>Service Discount</Text>
              </View>
              <View style={styles.col15Last}>
                <Text style={[styles.bold, styles.cellRight]}>
                  {serviceDiscountUnit === DISCOUNT_OPTIONS.value ? '$' : ''}
                  {formatNumber(serviceDiscount)}
                  {serviceDiscountUnit === DISCOUNT_OPTIONS.percentage ? '%' : ''}
                </Text>
              </View>
            </View>
          )}
        </View>
        {/* Materials Table */}
        {hasMaterials && (
          <>
            <View style={[styles.table, styles.font2]}>
              <View style={[styles.tableRow, styles.title]}>
                <View style={[styles.colLarge, { width: '85%' }]}>
                  <Text style={styles.bold}>Materials</Text>
                </View>
                <View style={styles.col15Last}>
                  <Text style={[styles.bold, styles.cellRight]}>Amount</Text>
                </View>
              </View>
              {materials.map((m, mi) => (
                <View key={mi} style={[styles.tableRow]}>
                  <View style={[styles.colLarge, { width: '85%' }]}>
                    <Text>{m.description}</Text>
                  </View>
                  <View style={styles.col15Last}>
                    <Text style={styles.cellRight}>${formatNumber(m.total || 0)}</Text>
                  </View>
                </View>
              ))}
              <View style={[styles.tableRow, styles.borderTop]}>
                <View style={[styles.colLarge, { width: '85%' }]}>
                  <Text style={[styles.bold, styles.cellRight]}>Subtotal</Text>
                </View>
                <View style={styles.col15Last}>
                  <Text style={[styles.bold, styles.cellRight]}>
                    ${formatNumber(materialsCost)}
                  </Text>
                </View>
              </View>
              {materialDiscount !== 0 && (
                <View style={styles.tableRow}>
                  <View style={[styles.colLarge, { width: '85%' }]}>
                    <Text style={[styles.bold, { marginLeft: '80%' }]}>Material Discount</Text>
                  </View>
                  <View style={styles.col15Last}>
                    <Text style={[styles.bold, styles.cellRight]}>
                      {materialDiscountUnit === DISCOUNT_OPTIONS.value ? '$' : ''}
                      {formatNumber(materialDiscount)}
                      {materialDiscountUnit === DISCOUNT_OPTIONS.percentage ? '%' : ''}
                    </Text>
                  </View>
                </View>
              )}
            </View>
          </>
        )}
        {/* Totals */}
        <View style={[styles.table, styles.font2, { marginTop: 30 }]} wrap={false}>
          <View style={[styles.tableRow, styles.borderTop]}>
            <View style={[styles.colLarge, { width: '85%' }]}>
              <Text style={[styles.bold, styles.cellRight]}>Services Total</Text>
            </View>
            <View style={styles.col15Last}>
              <Text style={[styles.bold, styles.cellRight]}>
                ${formatNumber(serviceCostAfterDiscount)}
              </Text>
            </View>
          </View>
          {hasMaterials && (
            <View style={styles.tableRow}>
              <View style={[styles.colLarge, { width: '85%' }]}>
                <Text style={[styles.bold, styles.cellRight]}>Materials Total</Text>
              </View>
              <View style={styles.col15Last}>
                <Text style={[styles.bold, styles.cellRight]}>
                  ${formatNumber(materialsCostAfterDiscount)}
                </Text>
              </View>
            </View>
          )}
          <View style={[styles.tableRow, styles.borderTop]}>
            <View style={[styles.colLarge, { width: '85%' }]}>
              <Text style={[styles.bold, styles.cellRight]}>ESTIMATE TOTAL</Text>
            </View>
            <View style={styles.col15Last}>
              <Text style={[styles.bold, styles.cellRight]}>
                ${formatNumber(totalCostAfterDiscount)}
              </Text>
            </View>
          </View>
        </View>
        <SignatureBlock />
        {/* Terms & Conditions */}
        <View wrap={false} style={[styles.termsContainer, styles.text9]}>
          <Text style={[styles.bold]}>Terms & Conditions</Text>
          <Text>
            This estimate will be honoured for 30 days from the day it was issued. All estimates for
            services may vary by 10%. Any deviation from original project scope or specifications as
            described in this estimate may result in a change in price. Should this occur, the
            client will be made aware of the change as soon as possible. Any additional costs
            exceeding the original estimate will be provided to the client for approval before these
            costs are incurred. Unless otherwise agreed upon, all media costs incurred by FKA are
            pre-billed upon approval or on the first day of the month in which the media is planned
            to run. Media costs for campaigns spanning less than six weeks may be billed in full
            upon approval or on the first day of the month in which the campaign begins. Media costs
            for longer campaigns will be pre-billed on the first day of each month in which the
            campaign runs. FKA utilizes the latest versions of Adobe Creative Cloud for general
            design and Sketch for interactive design. If the client requires copies of working files
            that are specifically compatible with anything other than the latest versions or other
            software file formats, additional charges may apply.
          </Text>
        </View>

        <Footer />
      </Page>
    </Document>
  );
};

export default EstimatePdf;
